import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomModal } from 'components/shared';
import { CustomModalProps } from 'components/shared/CustomModal/CustomModal';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks/useModal';
import { HTTPService } from 'service';
import { errorHandler } from 'utils/errorHandler';

import styles from './SupplierCancelPalletsPickUpModal.module.css';

type SupplierCancelPalletsPickUpModalProps = Omit<CustomModalProps, 'opened'> & {
   selectedPalletsPickUpId: string;
   onCancelPalletsPickUpSuccess: (palletsPickUpId: string) => void;
};

const SupplierCancelPalletsPickUpModal = ({
   selectedPalletsPickUpId,
   onCancelPalletsPickUpSuccess,
   onClose,
   ...props
}: SupplierCancelPalletsPickUpModalProps) => {
   const { t } = useTranslation();
   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleCloseModal = useCallback(() => {
      onClose();
   }, [onClose]);

   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose: handleCloseModal,
   });

   const handleCancelPalletsPickUp = async () => {
      try {
         setIsLoading(true);
         await HTTPService.rejectPalletPickUp(selectedPalletsPickUpId);
         onCancelPalletsPickUpSuccess(selectedPalletsPickUpId);
      } catch (error) {
         errorHandler(error, () => setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.COMPLETE_ACTION));
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <CustomModal
         {...props}
         opened
         onClose={closeModalHandler}
         icon="ALERT_TRIANGLE"
         innerTitle={t('supplierCancelPalletsPickUpModal.title')}
         error={errorInfo}
         isLoading={isLoading}
         primaryButtonProps={{
            text: t('supplierCancelPalletsPickUpModal.confirm'),
            variant: 'filled-danger',
            onClick: handleCancelPalletsPickUp,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'outlined-danger',
            onClick: closeModalHandler,
         }}
      >
         <>
            <p className={styles.plainText}>{t('supplierCancelPalletsPickUpModal.question')}</p>
            <p className={styles.boldBlackText}>
               {t('cancel_pallet_report_modal.irreversible')}
            </p>
         </>
      </CustomModal>
   );
};

export default SupplierCancelPalletsPickUpModal;
